'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { getMicrocopyValue } from '@lib/util/microcopy';
import { useCallback, useMemo } from 'react';
import { TreeAccordion } from 'ui-components';
import { getFacetTreeIdPrefix } from '../../facetTypes';
import { buildPriceBundle, resolvePriceLabel } from '../../utils/facetLabels';
import { PRICE_RANGES } from '../../utils/facetRanges';
import { FlatFacetShell } from '../FlatFacetShell';
import { useSearchControllers } from '../headless/SearchDefinitionContext';
import { NumericFlatFacetValue, useFlatFacet } from '../hooks/useFlatFacet';
import { PriceFacetProps } from './PriceFacet.types';

const getValueId = (v: NumericFlatFacetValue): string =>
  `price-${v.start}-${v.end}`;

export const PriceFacet = ({
  variant = 'horizontal',
  className,
  isOpen,
  onToggle,
}: PriceFacetProps) => {
  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
    searchValuesMicrocopy: MicrocopyItem[];
  }>();

  const priceLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.filter-price-label',
    'Price'
  );

  const clearLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.clear',
    'Clear'
  );

  const controllers = useSearchControllers();
  const priceFacet = controllers.usePriceFacet();
  const {
    values,
    selectedIds,
    selectedCount,
    isReady,
    handleClear,
    handleSelectMany,
  } = useFlatFacet({ facet: priceFacet, getValueId });

  const priceBundle = useMemo(
    () => buildPriceBundle(searchValuesMicrocopy),
    [searchValuesMicrocopy]
  );

  const getLabel = useCallback(
    (start: number, end: number) => {
      const range = PRICE_RANGES.find(
        (r) => r.start === start && r.end === end
      );
      if (!range) return `${start}-${end}`;
      return resolvePriceLabel(priceBundle, range.bundleKey, range.label);
    },
    [priceBundle]
  );

  const isDark = variant === 'mobile';

  const treeItems = useMemo(
    () =>
      values.map((v) => ({
        id: getValueId(v),
        label: getLabel(v.start, v.end),
        count: v.numberOfResults,
        disabled:
          !isReady || (v.numberOfResults === 0 && v.state !== 'selected'),
      })),
    [values, getLabel, isReady]
  );

  return (
    <FlatFacetShell
      title={priceLabel}
      clearLabel={clearLabel}
      selectedCount={selectedCount}
      onClear={handleClear}
      variant={variant}
      className={className}
      isOpen={isOpen}
      onToggle={onToggle}
      disabled={!isReady}
    >
      <TreeAccordion
        idPrefix={getFacetTreeIdPrefix(variant)}
        items={treeItems}
        selectionMode="multiple"
        selectedIds={selectedIds}
        onSelectMany={handleSelectMany}
        isDarkTheme={isDark}
        size={variant === 'sidebar' ? 'default' : 'sm'}
        reserveExpandSpace={variant !== 'horizontal'}
      />
    </FlatFacetShell>
  );
};
